import { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { searchEverything } from "@/api/polymarketApi";
import { swrJsonFetcher } from "@/hooks/swr/fetcher";
import { shortAddr } from "../../wallets/lib/format";
import { walletText } from "../components/Bits";
import { STATIC_DATA, useActiveMarkets, useLargeTrades } from "../lib/data";
import { cents, usd } from "../lib/format";
import { type DeskMarket, normalizeMarket } from "../lib/markets";
import { navigate, openMarket, openWallet } from "../lib/nav";
import { item, mergeItems, type SearchItem, type SearchKind, searchItems, viewItems } from "../lib/search";
import { useDebounced } from "../lib/useDebounced";
import { useWatchlist } from "../lib/watchlist";

const OPEN_EVENT = "polygains:palette";
const REMOTE_DEBOUNCE_MS = 180;
const REMOTE_MIN_CHARS = 2;
const TAPE_MIN_USD = 10_000;

export const openPalette = () => window.dispatchEvent(new Event(OPEN_EVENT));

const GROUP_LABEL: Record<SearchKind, string> = { view: "views", market: "markets", wallet: "wallets" };
const GROUP_ORDER: SearchKind[] = ["view", "market", "wallet"];

const marketItem = (m: DeskMarket): SearchItem =>
	item(
		"market",
		m.conditionId,
		m.question,
		[m.outcomes[0] ? `${m.outcomes[0].name} ${cents(m.outcomes[0].price)}` : null, `${usd(m.vol24)} 24h`, m.category].filter(Boolean).join(" · "),
		m.eventTitle ?? "",
		m.vol24,
	);

interface Named {
	address: string;
	name?: string | null;
	x_username?: string | null;
}

const walletItem = (w: Named, source: string): SearchItem =>
	item("wallet", w.address, walletText(w.address, w.name), `${shortAddr(w.address)} · ${source}`, `${w.name ?? ""} ${w.x_username ?? ""}`);

type Remote = { query: string; items: SearchItem[]; ms: number } | null;

/** Debounced Polymarket search. Each new query aborts the one before it. */
function useRemoteSearch(query: string) {
	const settled = useDebounced(query.trim(), REMOTE_DEBOUNCE_MS);
	const [remote, setRemote] = useState<Remote>(null);
	const [state, setState] = useState<"idle" | "busy" | "failed">("idle");

	useEffect(() => {
		if (settled.length < REMOTE_MIN_CHARS) {
			setRemote(null);
			setState("idle");
			return;
		}
		const ctl = new AbortController();
		const started = performance.now();
		setState("busy");
		searchEverything(settled, ctl.signal)
			.then((hits) => {
				if (ctl.signal.aborted) return;
				const now = Date.now();
				setRemote({
					query: settled,
					ms: performance.now() - started,
					items: [
						...hits.markets.map((m) => marketItem(normalizeMarket(m, now))),
						...hits.profiles.map((p) => walletItem({ address: p.proxyWallet as string, name: p.name || p.pseudonym }, "Polymarket profile")),
					],
				});
				setState("idle");
			})
			.catch(() => {
				if (!ctl.signal.aborted) setState("failed");
			});
		return () => ctl.abort();
	}, [settled]);

	const waiting = query.trim().length >= REMOTE_MIN_CHARS && (settled !== query.trim() || state === "busy");
	return { remote, failed: state === "failed", waiting };
}

function Palette({ onClose }: { onClose: () => void }) {
	const [q, setQ] = useState("");
	const [active, setActive] = useState(0);
	const input = useRef<HTMLInputElement>(null);
	const list = useRef<HTMLDivElement>(null);

	const { markets } = useActiveMarkets();
	const { data: tape } = useLargeTrades(TAPE_MIN_USD);
	const { data: segments } = useSWR<{ top_traders?: Named[] }>("data/segments.json", swrJsonFetcher, STATIC_DATA);
	const { data: copy } = useSWR<{ candidates?: Named[] }>("data/copy_candidates.json", swrJsonFetcher, STATIC_DATA);
	const { items: watched } = useWatchlist();

	const local = useMemo(
		() =>
			mergeItems(
				viewItems(),
				watched.map((w) => item("market", w.conditionId, w.question, "on your watchlist", "", Number.MAX_SAFE_INTEGER)),
				markets.map(marketItem),
				(segments?.top_traders ?? []).map((w) => walletItem(w, "top trader")),
				(copy?.candidates ?? []).map((w) => walletItem(w, "copy candidate")),
				(tape ?? []).map((t) => walletItem({ address: t.proxy_wallet, name: t.name || t.pseudonym }, "large trade")),
			),
		[markets, segments, copy, tape, watched],
	);

	const { remote, failed, waiting } = useRemoteSearch(q);

	// Timed here, in the render path, so the footer reports what a keystroke really costs.
	const { groups, localMs } = useMemo(() => {
		const started = performance.now();
		const merged = remote ? mergeItems(local, remote.items) : local;
		const found = searchItems(merged, q);
		return { groups: found, localMs: performance.now() - started };
	}, [local, remote, q]);

	const flat = useMemo(() => GROUP_ORDER.flatMap((k) => groups[k]), [groups]);
	useEffect(() => setActive(0), [q]);
	useEffect(() => {
		const opener = document.activeElement as HTMLElement | null;
		input.current?.focus();
		return () => opener?.focus?.();
	}, []);
	useEffect(() => {
		list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest" });
	}, [active, flat]);

	const choose = (i: SearchItem | undefined) => {
		if (!i) return;
		onClose();
		if (i.kind === "view") navigate(i.id);
		else if (i.kind === "market") openMarket(i.id);
		else openWallet(i.id);
	};

	const onKey = (e: KeyboardEvent) => {
		const down = e.key === "ArrowDown" || (e.ctrlKey && e.key === "n");
		const up = e.key === "ArrowUp" || (e.ctrlKey && e.key === "p");
		if (down || up) {
			e.preventDefault();
			if (flat.length) setActive((a) => (a + (down ? 1 : -1) + flat.length) % flat.length);
		} else if (e.key === "Enter") {
			e.preventDefault();
			choose(flat[active]);
		} else if (e.key === "Escape") {
			e.preventDefault();
			e.stopPropagation(); // the drawer behind must stay open
			onClose();
		}
	};

	let index = -1;
	return (
		<>
			<div className="fixed inset-0 z-[60] bg-black/70" onClick={onClose} aria-hidden="true" />
			<div
				role="dialog"
				aria-modal="true"
				aria-label="Search"
				className="fixed inset-x-2 top-2 z-[70] mx-auto flex max-h-[min(70dvh,560px)] max-w-[640px] flex-col border border-line-strong bg-ink shadow-2xl sm:top-[12dvh]"
			>
				<div className="flex h-9 shrink-0 items-center gap-2 border-b border-line px-2.5">
					<span aria-hidden="true" className="text-[11px] text-up">$</span>
					<input
						ref={input}
						role="combobox"
						aria-expanded="true"
						aria-controls="palette-list"
						aria-activedescendant={flat.length ? `palette-${active}` : undefined}
						aria-label="Search markets, wallets and views"
						placeholder="markets, wallets, views, 0x…"
						autoComplete="off"
						spellcheck={false}
						value={q}
						onInput={(e) => setQ((e.currentTarget as HTMLInputElement).value)}
						onKeyDown={onKey}
						className="h-full min-w-0 flex-1 bg-transparent text-[12px] text-bright placeholder:text-mute focus:outline-none"
					/>
					<button type="button" onClick={onClose} className="shrink-0 border border-line px-1.5 text-[10px] text-dim hover:text-bright">esc</button>
				</div>
				<div ref={list} id="palette-list" role="listbox" aria-label="Results" className="min-h-0 flex-1 overflow-y-auto">
					{GROUP_ORDER.map((kind) =>
						groups[kind].length ? (
							<div key={kind} role="group" aria-label={GROUP_LABEL[kind]}>
								<div className="sticky top-0 bg-ink px-2.5 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wide text-mute">{GROUP_LABEL[kind]}</div>
								{groups[kind].map((i) => {
									index += 1;
									const at = index;
									return (
										<div
											key={`${i.kind}:${i.id}`}
											id={`palette-${at}`}
											role="option"
											aria-selected={at === active}
											onMouseMove={() => setActive(at)}
											onClick={() => choose(i)}
											className={`flex cursor-pointer items-baseline justify-between gap-3 px-2.5 py-1 text-[11px] ${at === active ? "bg-raised shadow-[inset_2px_0_0_var(--color-up)]" : ""}`}
										>
											<span className="min-w-0 truncate text-bright">{i.title}</span>
											{i.sub ? <span className="num hidden shrink-0 text-[10px] text-dim sm:inline">{i.sub}</span> : null}
										</div>
									);
								})}
							</div>
						) : null,
					)}
					{flat.length === 0 ? <div className="px-2.5 py-3 text-[11px] text-dim">{waiting ? "searching Polymarket…" : "Nothing matches."}</div> : null}
				</div>
				<div className="num flex h-5 shrink-0 items-center justify-between gap-3 border-t border-line px-2.5 text-[10px] text-mute">
					<span className="hidden sm:inline">↑↓ move · ↵ open · esc close</span>
					<span className="ml-auto" data-palette-timing>
						local {localMs.toFixed(1)}ms
						{failed ? <span className="text-warn"> · Polymarket search failed, loaded data only</span> : waiting ? " · searching Polymarket" : remote ? ` · Polymarket ${remote.ms.toFixed(0)}ms` : ""}
					</span>
				</div>
			</div>
		</>
	);
}

/** Holds the hotkey. Data loads only once the palette opens. */
export function CommandPalette() {
	const [open, setOpen] = useState(false);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === "k") {
				e.preventDefault();
				setOpen((v) => !v);
			}
		};
		const onOpen = () => setOpen(true);
		window.addEventListener("keydown", onKey);
		window.addEventListener(OPEN_EVENT, onOpen);
		return () => {
			window.removeEventListener("keydown", onKey);
			window.removeEventListener(OPEN_EVENT, onOpen);
		};
	}, []);
	return open ? <Palette onClose={() => setOpen(false)} /> : null;
}

/** Visible way in, for phones and anyone who does not know the shortcut. */
export function PaletteButton() {
	return (
		<button type="button" onClick={openPalette} aria-label="Search" className="flex min-h-10 shrink-0 items-center gap-2 border border-line px-2 py-0.5 text-[11px] text-dim hover:border-up/50 hover:text-bright sm:min-h-0 sm:px-1.5">
			search <kbd className="hidden text-[10px] text-mute sm:inline">Ctrl K</kbd>
		</button>
	);
}
